// localStorage helpers for tracker, plan priority, and kids/pro progress.
// Once we wire up Supabase auth, swap these implementations to call the DB
// while keeping the same signatures so screens don't change.

// ── Kids lesson progress (completed lessons) ─────────────────────────────────
export const KIDS_PROGRESS_KEY = "moneyschool_kids_progress_v1";
export function loadKidsProgress(): Record<string, boolean> {
  try { const r = localStorage.getItem(KIDS_PROGRESS_KEY); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
}
export function saveKidsProgress(d: Record<string, boolean>) {
  try { localStorage.setItem(KIDS_PROGRESS_KEY, JSON.stringify(d)); } catch { /* quota */ }
}

// ── Pro lesson progress (completed lessons) ───────────────────────────────────
export const PRO_PROGRESS_KEY = "moneyschool_pro_progress_v1";
export function loadProProgress(): Record<string, boolean> {
  try { const r = localStorage.getItem(PRO_PROGRESS_KEY); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
}
export function saveProProgress(d: Record<string, boolean>) {
  try { localStorage.setItem(PRO_PROGRESS_KEY, JSON.stringify(d)); } catch { /* quota */ }
}

export const LESSON_HISTORY_KEY = "moneyschool_lesson_history_v1";

export type LessonEvent = {
  worldId: string;
  lessonIdx: number;
  title: string;
  worldLabel: string;
  worldEmoji: string;
  completedAt: string; // ISO date string
};

export function loadLessonHistory(): LessonEvent[] {
  try { const r = localStorage.getItem(LESSON_HISTORY_KEY); return r ? JSON.parse(r) : []; }
  catch { return []; }
}
export function saveLessonHistory(events: LessonEvent[]) {
  try { localStorage.setItem(LESSON_HISTORY_KEY, JSON.stringify(events)); } catch { /* quota */ }
}

export const TRACKER_KEY = "protracker_v2";
export const PLAN_PRIORITY_KEY = "moneyschool_plan_priority_v1";
export const REWARDS_KEY  = "moneyschool_rewards_v1";
export const MISSIONS_KEY = "moneyschool_missions_v1";
export const BUDGET_KEY   = "moneyschool_budget_v2";
export const CHORES_KEY   = "moneyschool_chores_v1";
export function loadChores() {
  try { const r = localStorage.getItem(CHORES_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveChores(d: any) {
  try { localStorage.setItem(CHORES_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
export function loadBudget() {
  try { const r = localStorage.getItem(BUDGET_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveBudget(d: any) {
  try { localStorage.setItem(BUDGET_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
export function loadMissions() {
  try { const r = localStorage.getItem(MISSIONS_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveMissions(d: any) {
  try { localStorage.setItem(MISSIONS_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
export function loadRewards() {
  try { const r = localStorage.getItem(REWARDS_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveRewards(d: any) {
  try { localStorage.setItem(REWARDS_KEY, JSON.stringify(d)); } catch { /* quota */ }
}

// ── Kids Savings Tracker ─────────────────────────────────────────────────────
// v3: 2-bucket model (spending / nest egg); goals funded by explicit transfer
export const SAVINGS_KEY = "moneyschool_savings_v3";
export function loadSavings() {
  try { const r = localStorage.getItem(SAVINGS_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveSavings(d: any) {
  try { localStorage.setItem(SAVINGS_KEY, JSON.stringify(d)); } catch { /* quota / disabled */ }
}

// ── Daily Challenge ──────────────────────────────────────────────────────────
// v2: adds longestStreak, shieldUsedDate, earnedBadges
export const DAILY_KEY = "moneyschool_daily_v2";
export function loadDaily() {
  try { const r = localStorage.getItem(DAILY_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveDaily(d: any) {
  try { localStorage.setItem(DAILY_KEY, JSON.stringify(d)); } catch { /* quota / disabled */ }
}

// ── Parent Portal ────────────────────────────────────────────────────────────
export const PARENT_KEY = "moneyschool_parent_v1";
export const PARENT_MILESTONES = [
  { id: "piggybank",   emoji: "🐷", label: "Has a piggy bank",          desc: "Started saving physically" },
  { id: "bankaccount", emoji: "🏦", label: "Opened a bank account",     desc: "Has a real savings account" },
  { id: "earnedmoney", emoji: "💼", label: "Earned their own money",     desc: "Chores, jobs, or small gig" },
  { id: "knowsbalance",emoji: "📊", label: "Knows their real balance",   desc: "Tracks their own money" },
  { id: "setgoal",     emoji: "🎯", label: "Set a real savings goal",    desc: "Working toward something specific" },
  { id: "donated",     emoji: "🤝", label: "Donated to a good cause",   desc: "Practiced generosity" },
];
export function loadParent() {
  try { const r = localStorage.getItem(PARENT_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveParent(d: any) {
  try { localStorage.setItem(PARENT_KEY, JSON.stringify(d)); } catch { /* quota / disabled */ }
}

export const EMPTY_CATS = {
  savings:    { label: "Cash Savings",     emoji: "🏦",  color: "#10b981", isAsset: true,  target: 0 },
  invest:     { label: "Investments",      emoji: "📈",  color: "#0ea5e9", isAsset: true,  target: 0 },
  emergency:  { label: "Emergency Fund",   emoji: "🛡️", color: "#f59e0b", isAsset: true,  target: 0 },
  creditCard: { label: "Credit Card Debt", emoji: "💳",  color: "#ef4444", isAsset: false, target: 0 },
  student:    { label: "Student Loans",    emoji: "🎓",  color: "#f97316", isAsset: false, target: 0 },
  carLoan:    { label: "Car Loan",         emoji: "🚗",  color: "#a78bfa", isAsset: false, target: 0 },
  other:      { label: "Other Debt",       emoji: "💸",  color: "#94a3b8", isAsset: false, target: 0 },
};

export function loadPriority() {
  try { const r = localStorage.getItem(PLAN_PRIORITY_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function savePriority(d: any) {
  try { localStorage.setItem(PLAN_PRIORITY_KEY, JSON.stringify(d)); } catch { /* quota / disabled */ }
}

export function loadTD() {
  try { const r = localStorage.getItem(TRACKER_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveTD(d: any) {
  try { localStorage.setItem(TRACKER_KEY, JSON.stringify(d)); } catch { /* quota / disabled */ }
}

// ── Pro Plan result + form inputs ───────────────────────────────────────────
// The full generated plan object is stored here so it survives page refresh.
// Cleared when the user explicitly resets their Pro data.
export const PLAN_RESULT_KEY = "moneyschool_plan_result_v1";
export function loadPlanResult(): any | null {
  try { const r = localStorage.getItem(PLAN_RESULT_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function savePlanResult(d: any) {
  try { localStorage.setItem(PLAN_RESULT_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
export function clearPlanResult() {
  try { localStorage.removeItem(PLAN_RESULT_KEY); } catch { /* ok */ }
}

export const PLAN_FORM_KEY = "moneyschool_plan_form_v1";
export function loadPlanForm(): any | null {
  try { const r = localStorage.getItem(PLAN_FORM_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function savePlanForm(d: any) {
  try { localStorage.setItem(PLAN_FORM_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
export function clearPlanForm() {
  try { localStorage.removeItem(PLAN_FORM_KEY); } catch { /* ok */ }
}

// ── Leaderboard display name ─────────────────────────────────────────────────
export const USERNAME_KEY = "moneyschool_username_v1";
export function loadUsername(): string | null {
  try { const v = localStorage.getItem(USERNAME_KEY); return v || null; }
  catch { return null; }
}
export function saveUsername(name: string) {
  try { localStorage.setItem(USERNAME_KEY, name); } catch { /* quota */ }
}
export function clearUsername() {
  try { localStorage.removeItem(USERNAME_KEY); } catch { /* ok */ }
}

// ── Admin: hidden users (Users tab) ──────────────────────────────────────────
// Client-side only — hides trial/test accounts from the admin Users list
// without touching the backend. Keyed by user id.
export const ADMIN_HIDDEN_USERS_KEY = "moneyschool_admin_hidden_users_v1";
export function loadHiddenUserIds(): string[] {
  try { const r = localStorage.getItem(ADMIN_HIDDEN_USERS_KEY); return r ? JSON.parse(r) : []; }
  catch { return []; }
}
export function saveHiddenUserIds(ids: string[]) {
  try { localStorage.setItem(ADMIN_HIDDEN_USERS_KEY, JSON.stringify(ids)); } catch { /* quota */ }
}

// ── Mini-game high scores ────────────────────────────────────────────────────
export const GAME_SCORES_KEY = "moneytalks_game_scores_v1";
export type GameRecord = { plays: number; best?: number };
export type GameScores = Record<string, GameRecord>;
export function loadGameScores(): GameScores {
  try { const r = localStorage.getItem(GAME_SCORES_KEY); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
}
export function saveGameScores(d: GameScores) {
  try { localStorage.setItem(GAME_SCORES_KEY, JSON.stringify(d)); } catch { /* quota */ }
}
